import React, { useEffect, useRef, useState } from 'react';
import {
  Dimensions, StyleSheet, Text, TextInput, View, Pressable, ScrollView,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useSelector } from 'react-redux';
import SearchIcon from '../../components/SearchIcon';
import Colors from '../../constants/Colors';
import getTemplateGroups from '../../requests/Assets/GetTemplateGroups';
import { RootTabScreenProps } from '../../types';
import getTemplateSpreadsheet from '../../requests/Assets/GetTemplateSpreadsheet';
import PlusIcon from '../../components/16pxIcons/PlusIcon';
import AddTemplateGroupModal from '../../components/AssetManagement/AddTemplateGroupModal';
import AlertModal from '../../components/AlertModal';
import SearchResultsPane from '../../components/AssetManagement/SearchResultsPane';
import ScreenEmptyState from '../../components/ScreenEmptyState';
import NoTemplateGroupsImage from '../../components/AssetManagement/NoTemplateGroupsImage';
import NoTemplatesImage from '../../components/AssetManagement/NoTemplatesImage';
import { logEvent } from '../../components/LogEvent';
import Style from '../../constants/Style';
import PressableStyled, { PressableState } from '../../components/PressableStyled';
import {
  AssetInstance, UserPermission, Template, TemplateGroup, UserGroup,
} from '../../constants/Types';
import getUserGroups from '../../requests/Users/GetUserGroups';
import {
  ATLEAST_ONE_USER_GROUP, noPermissionsSubtitle, noPermissionsTitle,
} from '../../constants/AlertMessages';
import TemplateSideBarRowV2 from '../../components/AssetManagement/TemplateSideBarRowV2';
import TemplateRowWebV2 from '../../components/AssetManagement/TemplateRowWebV2';
import CreateTemplate from './components/CreateTemplate';
import { openInNewTab } from '../../constants/Utils';
import ScrollViewStyled from '../../components/ScrollViewStyled';
import TemplatePresetModal from './components/TemplatePresetModal';
import {
  TemplateUpdateOption,
} from '../../components/AssetManagement/AddTemplateModal/components/CreationOptionList/components/CreationOption';
import SkeletonLoader from '../../components/SkeletonLoader';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import Button from '../../components/Button';
import { screenMaxWidth } from '../../components/TopSection';
import ExportButton from '../../components/ExportButton';
import ColumnsButton from '../../components/ColumnsButton';

export default function TemplateMgmtScreenV2({
  navigation, route,
}: RootTabScreenProps<AppScreen.TEMPLATE_MANAGEMENTV2>) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewTemplates: boolean = isAdmin || permissions?.includes(UserPermission.TEMPLATES_READ);
  const canAddTemplate: boolean = isAdmin || permissions?.includes(UserPermission.TEMPLATES_CREATE);
  const [assetsLoading, setAssetsLoading] = useState<boolean>(true);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(true);
  const [templateGroups,
    setTemplateGroups] = useState<TemplateGroup[] | undefined | null>(undefined);
  const [templates, setTemplates] = useState<Template[]>(null);
  const [assets, setAssets] = useState<AssetInstance[]>([]);
  const [exportDropdownShown, setExportDropdownShown] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetInstance>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [addGroupModalVisible, setAddGroupModalVisible] = useState<boolean>(false);
  const [templateSideBarContext, setTemplateSideBarContext] = useState<string>(null);
  const [searchResultsPaneFocused, setSearchResultsPaneFocused] = useState<boolean>(false);
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const [optionsMenuTemplateId, setOptionsMenuTemplateId] = useState<string | null>(null);
  const templateSearchField = useRef<TextInput | null>(null);
  const [addingTemplate, setAddingTemplate] = useState<boolean>(false);
  const [addTemplateOption, setAddTemplateOption] = useState<TemplateUpdateOption>(null);
  const [addTemplateId, setAddTemplateId] = useState<string | null>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [noDataAlertShown, setNoDataAlertShown] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [sidebarShown, setSidebarShown] = useState<boolean>(true);

  const showScreenAlert = (title: string, message?: string, error: boolean = true) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
  };

  const hideMenus: () => void = () => {
    setTemplateSideBarContext(null);
    setOptionsMenuTemplateId(null);
    setExportDropdownShown(false);
  };

  const exportError = (errorText: string) => {
    setExporting(false);
    showScreenAlert('Error', errorText ?? 'Unable to export data at the moment. Please try again later.');
  };

  const exportData = (buildingId?: string) => {
    setExporting(true);
    setExportDropdownShown(false);
    getTemplateSpreadsheet(buildingId).then((response) => {
      if (response && response.url?.length > 0) openInNewTab(response.url);
      else exportError(response?.errorText);
      setExporting(false);
    }).catch((error) => exportError(error?.message));
  };

  const noTemplatesFound: () => void = () => {
    if (!noDataAlertShown) {
      setTemplates([]);
      setTemplateGroups(null);
      showScreenAlert('Error', 'Could not load asset templates at the moment. Please try again later.');
      setNoDataAlertShown(true);
      setTemplatesLoading(false);
    }
  };

  const setSortedTemplateGroups = (updatedTemplateGroupList: TemplateGroup[]) => {
    setTemplateGroups(updatedTemplateGroupList.sort((a: TemplateGroup, b: TemplateGroup) => a.Name
      ?.toLowerCase().localeCompare(b.Name?.toLowerCase())));
  };

  useEffect(() => {
    if (!exportDropdownShown) return;
    setTemplateSideBarContext(null);
    setOptionsMenuTemplateId(null);
  }, [exportDropdownShown]);

  useEffect(() => {
    if (!templateSideBarContext) return;
    setExportDropdownShown(false);
    setOptionsMenuTemplateId(null);
  }, [templateSideBarContext]);

  useEffect(() => {
    if (!optionsMenuTemplateId) return;
    setTemplateSideBarContext(null);
    setExportDropdownShown(false);
  }, [optionsMenuTemplateId]);

  useEffect(() => {
    getUserGroups().then((response: { UserGroups?: UserGroup[] }) => {
      setUserGroups(response?.UserGroups
        ?.sort((prev: UserGroup, next: UserGroup) => prev.Name.toLowerCase()
          .localeCompare(next.Name.toLowerCase())) ?? []);
    });
    getTemplateGroups().then((response) => {
      if (!response?.groups) noTemplatesFound();
      else {
        setSortedTemplateGroups(response?.groups);
        const templateDataList: Template[] = response?.groups?.reduce((prev, group) => {
          if (group?.buttonPrototypes?.length) return prev.concat(group.buttonPrototypes);
          return prev;
        }, []) ?? [];
        setTemplates(templateDataList);
      }
    }).catch(noTemplatesFound);
    const unsubscribe = navigation.addListener('blur', hideMenus);
    return unsubscribe;
  }, []);

  useEffect(() => {
    setSearchValue('');
  }, [selectedTemplate, selectedGroupId]);

  useEffect(hideMenus, [
    addingTemplate, selectedTemplate, selectedGroupId, searchValue, addGroupModalVisible]);

  useEffect(() => {
    if (templatesLoading && templates) {
      setTemplatesLoading(false);
    }
  }, [templates]);

  useEffect(() => {
    if (!(canViewTemplates && templates && !templatesLoading)) return;
    const templateId: string = (route?.params as any)?.templateId;

    if (templateId) {
      setSelectedTemplate(null);
      setAssets([]);
      if (templateId) {
        const filteredTemplate: Template | null = templates
          .filter((template: Template) => template.id === templateId)[0];
        if (filteredTemplate) {
          setSelectedTemplate(filteredTemplate);
          setAssets([]);
          setAssetsLoading(true);
        } else {
          setSelectedTemplate(null);
          showScreenAlert('Error', 'Linked template could not be found. It may have been deleted.');
        }
      }

      navigation.setParams({ templateId: undefined, assetId: undefined } as any);
    }
  }, [route, templates, templatesLoading]);

  useEffect(() => {
    if (!(selectedAssetId && selectedTemplate && !assetsLoading)) return;
    const filteredAsset = assets
      ?.filter((asset: AssetInstance) => asset.id === selectedAssetId)[0];
    if (filteredAsset) setSelectedAsset(filteredAsset);
    else {
      showScreenAlert('Error', 'Linked asset could not be found. It may have been deleted.');
    }
    setSelectedAssetId(null);
  }, [selectedTemplate, selectedAssetId, assetsLoading]);

  const updateTemplateLocally = (template: Template, add: boolean, groupID: string) => {
    if (!template?.id) return;
    setTemplatesLoading(true);
    if (add) {
      setTemplateGroups(templateGroups.map((group: TemplateGroup) => ({
        ...group,
        buttonPrototypes: group.ButtonPrototypeGroupId === groupID
          ? [...group.buttonPrototypes, template] : group.buttonPrototypes,
      })));
      setTemplates([...templates, template]);
    } else {
      const updatedTemplateGroups: TemplateGroup[] = templateGroups
        ?.map((group: TemplateGroup) => ({
          ...group,
          buttonPrototypes: group.buttonPrototypes.filter((t: Template) => t.id !== template.id),
        }));
      updatedTemplateGroups.forEach((group: TemplateGroup) => {
        if (group.ButtonPrototypeGroupId === groupID) {
          group.buttonPrototypes.push(template);
        }
      });
      setTemplateGroups(updatedTemplateGroups);
      setTemplates(templates
        .map((t: Template) => ({ ...t, ...(t.id === template.id ? template : {}) })));
    }
    if (selectedTemplate && selectedTemplate.id === template.id) setSelectedTemplate(template);
  };

  const showAssets = (template: Template) => {
    navigation.navigate(
      AppScreen.ASSET_MANAGEMENT as any,
      { templateId: template.id, templateName: template.name },
    );
  };

  const renderTemplatesFromList = (templatesList) => <ScrollView
    testID='template-list'
    style={styles.templateList}
  >
    {templatesList
      ?.sort((a: Template, b: Template) => a.name?.toLowerCase()
        .localeCompare(b.name?.toLowerCase())).map((template, index: number) => !template.deleted
          && <TemplateRowWebV2
            {...{
              templateGroups,
              template,
              userGroups,
              index,
              optionsMenuTemplateId,
              setOptionsMenuTemplateId,
            }}
            closeMenu={hideMenus}
            showParentAlert={showScreenAlert}
            update={updateTemplateLocally}
            setLinkAssetsView={() => { }}
            setSelectedTitle={() => { }}
            setSelected={() => setSelectedTemplate(template)}
            deleteAction={(id: string) => {
              const updatedTemplateGroups = [...templateGroups];
              updatedTemplateGroups.forEach((group) => {
                const updatedTemplates = (group.buttonPrototypes ?? [])
                  .filter((currentTemplate) => currentTemplate.id !== id);
                group.buttonPrototypes = updatedTemplates;
              });
              setTemplateGroups(updatedTemplateGroups);
              const newArray = templates.filter((t: Template) => t.id !== id);
              setTemplates(newArray);
            }}
            key={`template_${template.id}`}
          />)}
    {!templatesLoading && templateGroups?.length > 0
      && !((selectedGroupId ? templateGroups.filter((group) => group.ButtonPrototypeGroupId
        === selectedGroupId)[0]?.buttonPrototypes : templates)?.length > 0)
      && <ScreenEmptyState
        image={<NoTemplatesImage />}
        title={selectedGroupId ? 'No templates in this template group' : 'Add your first template'}
        subtitle='Contact your system administrator to add a template to work with'
        adminSubtitle='Add a template to work with'
        actionAvailable={canAddTemplate}
        buttonTitle='Add template'
        buttonAction={() => {
          logEvent('AssetManagementWeb', 'add_template_button');
          if (!userGroups.length) {
            showScreenAlert(
              'No user group found',
              ATLEAST_ONE_USER_GROUP,
              true,
            );
          } else setAddingTemplate(true);
        }}
        style={styles.emptyState}
      />}
  </ScrollView>;

  const renderTemplates = () => {
    if (templatesLoading) {
      return <View style={{ marginHorizontal: 24 }}>
        <SkeletonLoader delay={100} />
        <SkeletonLoader delay={200} />
        <SkeletonLoader delay={300} />
        <SkeletonLoader delay={400} />
        <SkeletonLoader delay={500} />
        <SkeletonLoader delay={600} />
        <SkeletonLoader delay={700} />
      </View>;
    }
    if (!selectedGroupId) return renderTemplatesFromList(templates);
    return renderTemplatesFromList(
      templateGroups?.filter((group) => group.ButtonPrototypeGroupId === selectedGroupId)[0]
        ?.buttonPrototypes ?? templates,
    );
  };

  const renderSearchPane = () => (searchValue?.length > 0 && <SearchResultsPane
    focused={searchResultsPaneFocused}
    blur={templateSearchField?.current?.focus}
    {...{
      searchValue, templates, templateGroups, selectedAsset, selectedTemplate, setSearchValue,
    }}
    selectTemplate={(template: Template) => showAssets(template)}
    selectAsset={setSelectedAsset}
    selectTemplateGroup={setSelectedGroupId}
    showAssets={() => { }}
    selectedTemplateGroup={selectedGroupId}
    assetsShown={false}
  />);

  const renderManageGroupsView = () => (
    <Animatable.View
      animation={'fadeIn'}
      duration={300}
      style={styles.content}
    >
      {sidebarShown && <View style={styles.sidebar}>
        <View style={Style.row}>
          <Text style={styles.templateGroupsTitle}>
            {'TEMPLATE GROUPS'}
          </Text>
          {canAddTemplate && <PressableStyled
            style={styles.addTemplateGroupsButton}
            onPress={() => {
              logEvent('AssetManagementWeb', 'Add_Template_Group');
              setAddGroupModalVisible(!addGroupModalVisible);
            }}
            testID='am-add-template-group-btn'
          >
            <PlusIcon color={Colors.light.hereworksBlue500} />
          </PressableStyled>}
          <AddTemplateGroupModal
            {...{ templateGroups }}
            isVisible={addGroupModalVisible}
            hide={() => setAddGroupModalVisible(false)}
            update={(Name: string, ButtonPrototypeGroupId: string, create: boolean) => {
              const updatedTemplateGroups = [...templateGroups];
              if (create) {
                updatedTemplateGroups.push({
                  Name,
                  ButtonPrototypeGroupId,
                  buttonPrototypes: [],
                });
              } else {
                updatedTemplateGroups?.forEach((group) => {
                  if (group.ButtonPrototypeGroupId === ButtonPrototypeGroupId) {
                    group.Name = Name;
                  }
                });
              }
              setSortedTemplateGroups(updatedTemplateGroups);
            }}
          />
        </View>
        <View style={styles.separator} />
        <ScrollViewStyled contentContainerStyle={{ flexDirection: 'column-reverse' }}>
          {templateGroups?.length > 0
            ? templateGroups?.sort((prev, next) => next.Name?.toLowerCase()
              .localeCompare(prev.Name?.toLowerCase()))
              .map((item: TemplateGroup) => <TemplateSideBarRowV2
                {...{
                  selectedGroupId, templateGroups, showScreenAlert,
                }}
                group={item}
                onPress={() => {
                  if (selectedGroupId === item.ButtonPrototypeGroupId) setSelectedGroupId(null);
                  else setSelectedGroupId(item.ButtonPrototypeGroupId);
                }}
                contextMenuOpen={() => setTemplateSideBarContext(item.ButtonPrototypeGroupId)}
                contextMenuClose={hideMenus}
                currentContextMenu={templateSideBarContext}
                update={(newName: string, id: string, create?: boolean, toDelete?: boolean) => {
                  if (create) {
                    setSortedTemplateGroups([
                      ...templateGroups,
                      { Name: newName, ButtonPrototypeGroupId: id, buttonPrototypes: [] },
                    ]);
                  } else if (toDelete) {
                    setSortedTemplateGroups(templateGroups?.filter((group: TemplateGroup) => group
                      .ButtonPrototypeGroupId !== id));
                  } else {
                    setSortedTemplateGroups(templateGroups?.map((group: TemplateGroup) => (group
                      .ButtonPrototypeGroupId === id ? { ...group, Name: newName } : group)));
                  }
                }}
                key={`template_group_menu_item_${item.ButtonPrototypeGroupId}`}
              />) : <View style={styles.noTemplateGroupsMessage}>
              <Text style={styles.noTemplatesSubtitle}>
                {templateGroups && 'No groups'}
                {templateGroups === undefined && 'Loading template groups...'}
                {templateGroups === null && 'Unable to load template groups'}
              </Text>
            </View>}
        </ScrollViewStyled>
      </View>}
      <View style={{ flex: 7 }}>
        <View style={styles.searchPaneContainer}>
          <View style={styles.searchBox}>
            <SearchIcon />
            <TextInput
              placeholder="Search"
              placeholderTextColor={Colors.light.hereworksBlack400}
              value={searchValue}
              style={styles.searchText}
              ref={templateSearchField}
              onFocus={() => setSearchResultsPaneFocused(false)}
              onChangeText={(text: string) => {
                setSearchValue(text);
                setSearchResultsPaneFocused(false);
              }}
              onKeyPress={(e) => {
                // @ts-ignore
                if (e.code === 'ArrowDown') setSearchResultsPaneFocused(true);
              }}
            />
          </View>
          {canAddTemplate && <Button
            style={styles.createButton}
            onPress={() => {
              logEvent('AssetManagementWeb', 'Create_new_template');
              if (!userGroups.length) {
                showScreenAlert(
                  'No user group found',
                  ATLEAST_ONE_USER_GROUP,
                  true,
                );
              } else setAddingTemplate(true);
            }}
            title={'Add template'}
            children={<PlusIcon style={{ marginStart: 8 }} />}
            affirmative
          />}
        </View>
        {renderSearchPane()}
        <View style={styles.contentHeader}>
          <Text style={styles.templateGroupTitle}>
            {templateGroups?.filter((group: TemplateGroup) => group.ButtonPrototypeGroupId
              === selectedGroupId)[0]?.Name ?? 'All templates'}
          </Text>
          <ColumnsButton
            onPress={() => setSidebarShown(!sidebarShown)}
            tooltipText={sidebarShown ? 'Collapse sidebar' : 'Expand sidebar'}
          />
        </View>
        <View style={styles.listHeader}>
          <Text style={[styles.headerText, { flex: 3 }]}>{'TEMPLATES'}</Text>
          <Text style={[styles.headerText, { flex: 4 }]}>{'ASSET COUNT'}</Text>
        </View>
        {renderTemplates()}
        {!templatesLoading && templateGroups?.length === 0 && <ScreenEmptyState
          image={<NoTemplateGroupsImage />}
          title='Start with a template group'
          subtitle={'In template groups you will collect your templates.\nContact your system administrator to create a template group'}
          adminSubtitle={'In template groups you will collect your templates.\nPlease create a template group'}
          actionAvailable={canAddTemplate}
          buttonTitle='Create template group'
          buttonAction={() => setAddGroupModalVisible(true)}
        />}
      </View>
    </Animatable.View>
  );

  if (!canViewTemplates) {
    return <View style={styles.container}>
      <ScreenEmptyState
        title={noPermissionsTitle('templates')}
        subtitle={noPermissionsSubtitle('templates')}
        image={<NoTemplatesImage />}
      />
    </View>;
  }

  return <Pressable
    onPress={hideMenus}
    style={styles.container}
  >
    {(addTemplateOption || selectedTemplate) ? <CreateTemplate
      templateUpdateOption={selectedTemplate ? TemplateUpdateOption.EDIT : addTemplateOption}
      selectedTemplateId={selectedTemplate?.id ?? addTemplateId}
      hide={() => {
        setAddingTemplate(false);
        setAddTemplateOption(null);
        setSelectedTemplate(null);
      }}
      {...{
        templateGroups, userGroups, templates, selectedGroupId,
      }}
      users={[]}
      update={updateTemplateLocally}
      showParentAlert={showScreenAlert}
      templateToUpdate={selectedTemplate}
      deleteAction={(id: string) => {
        const updatedTemplateGroups: TemplateGroup[] = [...templateGroups];
        updatedTemplateGroups.forEach((group: TemplateGroup) => {
          const updatedTemplates = (group.buttonPrototypes ?? [])
            .filter((currentTemplate) => currentTemplate.id !== id);
          group.buttonPrototypes = updatedTemplates;
        });
        setTemplateGroups(updatedTemplateGroups);
        setTemplates([...templates.filter((t: Template) => t.id !== id)]);
      }}
    /> : <View style={{ flex: 1, maxWidth: screenMaxWidth - 74 }}>
      <View style={[Style.row, { zIndex: 2 }]}>
        <Text style={styles.screenTitle}>
          {'Template management'}
        </Text>
        <>
          <ExportButton
            onPress={() => {
              setTemplateSideBarContext(null);
              logEvent('AssetManagementWeb', 'Export');
              if (Object.values(buildings)?.length > 0) {
                setExportDropdownShown(!exportDropdownShown);
              } else exportData();
            }}
            {...{ exporting }}
          />
          {exportDropdownShown && <View style={styles.exportDropdown}>
            <Pressable
              onPress={() => exportData(selectedBuildingId)}
              style={({ hovered, pressed }: PressableState) => [
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
            >
              <Text style={styles.exportOptionText}>{'Export data for current building'}</Text>
            </Pressable>
            <Pressable
              onPress={() => exportData()}
              style={({ hovered, pressed }: PressableState) => [
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
            >
              <Text style={styles.exportOptionText}>{'Export data for all buildings'}</Text>
            </Pressable>
          </View>}
        </>
      </View>
      {renderManageGroupsView()}
    </View>}
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      setModalVisible={setAlertVisible}
      actionTitle={userGroups.length ? '' : 'Go to user management'}
      action={userGroups.length ? null : () => {
        navigation.navigate(AppScreen.USER_MANAGEMENT as any);
      }}
      isMessage={!userGroups.length}
    />
    {!addTemplateOption && <TemplatePresetModal
      isVisible={addingTemplate}
      hide={() => setAddingTemplate(false)}
      {...{ templates, setAddTemplateOption }}
      setSelectedTemplateId={setAddTemplateId}
    />}
  </Pressable>;
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 40,
    paddingTop: 48,
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
    height: Dimensions.get('screen').height - 120,
    cursor: 'auto',
  },
  content: {
    borderRadius: 16,
    marginVertical: 32,
    overflow: 'hidden',
    flexDirection: 'row',
    alignSelf: 'stretch',
    backgroundColor: '#fff',
    flex: 1,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
  },
  selectedTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 18,
    lineHeight: 27,
    color: Colors.light.hereworksBlack600,
  },
  createButton: {
    width: 168,
    marginStart: 40,
  },
  createButtonTitle: {
    color: '#fff',
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    marginStart: 24,
    marginEnd: 10,
    marginVertical: 12,
  },
  searchBox: {
    height: 40,
    flex: 1,
    borderRadius: 24,
    backgroundColor: '#fff',
    borderColor: Colors.light.hereworksPaleBlue900,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    paddingStart: 12,
    padding: 4,
  },
  searchText: {
    flex: 1,
    alignSelf: 'stretch',
    color: Colors.light.hereworksBlack400,
    marginStart: 4,
    paddingStart: 8,
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    alignItems: 'center',
    textAlignVertical: 'center',
    outlineWidth: 0,
    borderRadius: 24,
  },
  separator: {
    backgroundColor: Colors.light.hereworksBlue100,
    height: 1,
    width: '100%',
    marginBottom: 8,
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 16,
    letterSpacing: 0.1,
    color: '#FFF',
  },
  searchPaneContainer: {
    ...Style.row,
    paddingHorizontal: 24,
    height: 62,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  contentHeader: {
    ...Style.row,
    margin: 24,
  },
  noTemplatesSubtitle: {
    fontSize: 12,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
  },
  noTemplateGroupsMessage: {
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
    paddingHorizontal: 12,
    paddingVertical: 16,
    margin: 16,
  },
  addTemplateGroupsButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue500,
    padding: 4,
    marginEnd: 18,
  },
  templateGroupsTitle: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 20,
    color: Colors.light.hereworksBlack500,
    marginTop: 24,
    marginStart: 24,
    marginBottom: 17,
  },
  exportDropdown: {
    position: 'absolute',
    top: 48,
    end: 0,
    backgroundColor: '#FFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
  },
  exportOptionText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  sidebar: {
    minWidth: 240,
    flex: 2,
    backgroundColor: '#fff',
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue100,
  },
  screenTitle: {
    fontSize: 32,
    lineHeight: 48,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
  },
  templateGroupTitle: {
    fontSize: 22,
    lineHeight: 33,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    letterSpacing: 0,
  },
  listHeader: {
    ...Style.row,
    marginHorizontal: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopStartRadius: 8,
    borderTopEndRadius: 8,
    backgroundColor: Colors.light.hereworksBlue400,
  },
  templateList: {
    flex: 1,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    marginHorizontal: 24,
    borderBottomEndRadius: 8,
    borderBottomStartRadius: 8,
  },
  emptyState: {
    flex: 1,
    alignSelf: 'center',
    marginVertical: '10%',
  },
});
